import React, { ReactNode, useMemo, Children, isValidElement, cloneElement } from 'react'
import {
  Document,
  Image,
  Link,
  Page,
  StyleSheet,
  Text,
  View,
  pdf,
} from '@react-pdf/renderer'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { ReportPdfModel, ReportPdfTocEntry } from './pdfExport'
import { API_BASE_URL, getAuthHeader } from './api'
import { cleanPdfMarkdown } from './markdownCleanup'
import { resolveAssetUrl } from './urlFormatting'

const SAFE_MARKDOWN_ELEMENTS = [
  'h1',
  'h2',
  'h3',
  'p',
  'strong',
  'em',
  'a',
  'ul',
  'ol',
  'li',
  'blockquote',
  'code',
  'pre',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
  'hr',
  'del',
  's',
  'br',
  'input',
] as const

const styles = StyleSheet.create({
  // ── Cover page ──
  coverPage: {
    backgroundColor: '#0f172a',
  },
  coverCanvas: {
    width: '100%',
    height: '100%',
    position: 'relative',
  },
  coverImageFrame: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
  coverBg: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: '#0f172a',
  },
  coverBgNoImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: '#1e293b',
  },
  coverImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  coverOverlayLight: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(15, 23, 42, 0.25)',
  },
  coverOverlayHeavy: {
    position: 'absolute',
    top: 352,
    left: 0,
    width: '100%',
    bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.72)',
  },
  coverContent: {
    position: 'absolute',
    left: 56,
    right: 56,
    bottom: 64,
  },
  coverKicker: {
    fontFamily: 'Helvetica',
    fontSize: 11,
    letterSpacing: 2.4,
    textTransform: 'uppercase',
    color: '#e2e8f0',
    marginBottom: 12,
  },
  coverCategory: {
    alignSelf: 'flex-start',
    backgroundColor: '#6366f1',
    borderRadius: 999,
    paddingHorizontal: 14,
    paddingVertical: 6,
    marginBottom: 14,
  },
  coverCategoryText: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 9,
    textTransform: 'uppercase',
    color: '#ffffff',
    letterSpacing: 1.2,
  },
  coverTitle: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 42,
    lineHeight: 1.1,
    color: '#ffffff',
  },
  coverRule: {
    width: 80,
    height: 3,
    borderRadius: 999,
    backgroundColor: '#818cf8',
    marginTop: 20,
    marginBottom: 20,
  },
  coverMeta: {
    fontFamily: 'Helvetica',
    fontSize: 12,
    lineHeight: 1.55,
    color: '#e2e8f0',
  },

  // ── Table of contents ──
  tocPage: {
    backgroundColor: '#ffffff',
    paddingTop: 54,
    paddingBottom: 54,
    paddingHorizontal: 52,
    color: '#0f172a',
  },
  tocHeader: {
    borderBottomWidth: 2,
    borderBottomColor: '#0f172a',
    paddingBottom: 14,
    marginBottom: 28,
  },
  tocTitle: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 26,
    color: '#0f172a',
  },
  tocList: {
    marginTop: 0,
  },
  tocNumberBox: {
    width: 34,
    marginRight: 10,
  },
  tocContent: {
    flexDirection: 'row',
    alignItems: 'baseline',
    flex: 1,
  },
  tocItemLevel2: {
    flexDirection: 'row',
    alignItems: 'baseline',
    paddingVertical: 9,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  tocItemLevel3: {
    flexDirection: 'row',
    alignItems: 'baseline',
    paddingVertical: 6,
    paddingLeft: 24,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
  },
  tocNumberText: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 11.5,
    color: '#6366f1',
  },
  tocLinkLevel2: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 12.5,
    color: '#1e293b',
    textDecoration: 'none',
    flexShrink: 1,
  },
  tocLinkLevel3: {
    fontFamily: 'Helvetica',
    fontSize: 11.5,
    color: '#475569',
    textDecoration: 'none',
    flexShrink: 1,
  },
  tocPageNumber: {
    width: 28,
    textAlign: 'right',
    fontFamily: 'Helvetica-Bold',
    fontSize: 11,
    color: '#475569',
  },

  // ── Content pages ──
  contentPage: {
    backgroundColor: '#ffffff',
    paddingTop: 54,
    paddingBottom: 58,
    paddingHorizontal: 52,
    color: '#0f172a',
    fontFamily: 'Times-Roman',
    fontSize: 11.5,
    lineHeight: 1.7,
  },
  headerTitle: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 23,
    lineHeight: 1.2,
    marginBottom: 6,
    color: '#0f172a',
  },
  headerMeta: {
    fontFamily: 'Helvetica',
    fontSize: 10.5,
    color: '#64748b',
    marginBottom: 22,
  },
  section: {
    marginBottom: 26,
  },
  sectionHeading: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 22,
    lineHeight: 1.25,
    color: '#0f172a',
    marginBottom: 4,
  },
  sectionRule: {
    height: 1,
    backgroundColor: '#e2e8f0',
    marginBottom: 12,
  },
  reportHeadingLevel2: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 30,
    lineHeight: 1.18,
    color: '#0f172a',
    marginTop: 32,
    marginBottom: 8,
  },
  reportHeadingLevel1Container: {
    marginTop: 38,
    marginBottom: 20,
  },
  reportHeadingLevel1: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 34,
    lineHeight: 1.12,
    color: '#0f172a',
    letterSpacing: -0.2,
    marginBottom: 10,
  },
  reportHeadingLevel1Rule: {
    width: 84,
    height: 4,
    borderRadius: 999,
    backgroundColor: '#475569',
  },
  reportHeadingLevel2Rule: {
    height: 1,
    backgroundColor: '#e2e8f0',
    marginBottom: 10,
  },
  reportHeadingLevel3: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 21,
    lineHeight: 1.25,
    color: '#1e293b',
    marginTop: 22,
    marginBottom: 6,
  },
  paragraph: {
    fontFamily: 'Times-Roman',
    fontSize: 11.5,
    lineHeight: 1.7,
    color: '#1e293b',
    marginBottom: 12,
  },
  strong: {
    fontFamily: 'Helvetica-Bold',
    color: '#0f172a',
  },
  em: {
    fontFamily: 'Times-Italic',
  },
  list: {
    marginBottom: 14,
  },
  listItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 7,
  },
  listBullet: {
    width: 20,
    fontFamily: 'Helvetica',
    fontSize: 11.5,
    color: '#6366f1',
  },
  listItemBody: {
    flex: 1,
  },
  listItemText: {
    fontFamily: 'Times-Roman',
    fontSize: 11.5,
    lineHeight: 1.7,
    color: '#1e293b',
  },
  blockquote: {
    borderLeftWidth: 3,
    borderLeftColor: '#6366f1',
    backgroundColor: '#f8fafc',
    paddingLeft: 14,
    paddingVertical: 8,
    paddingRight: 10,
    marginBottom: 14,
  },
  inlineCode: {
    fontFamily: 'Courier',
    fontSize: 10.5,
    backgroundColor: '#f1f5f9',
    color: '#0f172a',
  },
  codeBlock: {
    fontFamily: 'Courier',
    fontSize: 10,
    lineHeight: 1.55,
    color: '#0f172a',
  },
  pre: {
    backgroundColor: '#f8fafc',
    borderRadius: 6,
    padding: 12,
    marginBottom: 14,
  },
  link: {
    color: '#4f46e5',
    textDecoration: 'none',
  },
  table: {
    width: '100%',
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 6,
    overflow: 'hidden',
    marginBottom: 14,
  },
  tableHead: {
    backgroundColor: '#f8fafc',
  },
  tableBody: {},
  tableRow: {
    flexDirection: 'row',
  },
  tableCellHeader: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#cbd5e1',
  },
  tableCell: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  tableCellText: {
    fontFamily: 'Times-Roman',
    fontSize: 10.5,
    lineHeight: 1.5,
    color: '#0f172a',
  },
  tableHeaderText: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 10.5,
    lineHeight: 1.4,
    color: '#1e293b',
  },
  findingsList: {
    marginTop: 4,
  },
  referencesList: {
    marginTop: 8,
  },
  referenceItem: {
    flexDirection: 'row',
    alignItems: 'baseline',
    marginBottom: 6,
  },
  referenceNumber: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 10.5,
    color: '#1e293b',
    width: 28,
  },
  referenceLink: {
    fontFamily: 'Times-Roman',
    fontSize: 10.5,
    lineHeight: 1.5,
    color: '#4338ca',
    textDecoration: 'none',
    flex: 1,
  },
  pageNumber: {
    position: 'absolute',
    bottom: 22,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontFamily: 'Helvetica',
    fontSize: 9.5,
    color: '#94a3b8',
  },
  pageReferenceProbe: {
    position: 'absolute',
    color: 'transparent',
    fontSize: 0,
    lineHeight: 0,
  },
  reportSectionAnchor: {
    margin: 0,
    padding: 0,
  },
})

function stripRootWhitespaceMarkdown() {
  return (tree: any) => {
    if (!tree || tree.type !== 'root' || !Array.isArray(tree.children)) {
      return
    }

    tree.children = tree.children.filter((child: any) => {
      if (!child || child.type !== 'text') {
        return true
      }

      return Boolean(String(child.value || '').trim())
    })
  }
}

function stripPdfNewlineTextNodes() {
  function visit(node: any) {
    if (!node || !Array.isArray(node.children)) {
      return
    }

    node.children = node.children.filter((child: any) => {
      if (!child || child.type !== 'text') {
        return true
      }

      const value = String(child.value || '')
      return !(value.includes('\n') && !value.trim())
    })

    node.children.forEach(visit)
  }

  return (tree: any) => {
    visit(tree)
  }
}

const PDF_REMARK_PLUGINS: any = [
  [remarkGfm, { singleTilde: false }],
  stripRootWhitespaceMarkdown,
]

const PDF_REHYPE_PLUGINS: any = [stripPdfNewlineTextNodes]

export function normalizeMarkdownForPdf(markdown: string): string {
  return cleanPdfMarkdown(markdown)
}

function getPlainText(children: ReactNode): string {
  let text = ''

  Children.forEach(children, (child) => {
    if (typeof child === 'string' || typeof child === 'number') {
      text += String(child)
      return
    }

    if (isValidElement(child)) {
      text += getPlainText(child.props.children)
    }
  })

  return text.trim()
}

function sanitizeInlineChildren(children: ReactNode): ReactNode[] {
  const nodes: ReactNode[] = []

  Children.forEach(children, (child, index) => {
    if (child == null || typeof child === 'boolean') {
      return
    }

    if (typeof child === 'string' || typeof child === 'number') {
      nodes.push(String(child))
      return
    }

    if (!isValidElement(child)) {
      return
    }

    if (typeof child.type === 'string') {
      const plainText = getPlainText(child.props?.children)
      if (plainText) {
        nodes.push(plainText)
      }
      return
    }

    if (child.props?.children !== undefined) {
      nodes.push(
        cloneElement(
          child as React.ReactElement<any>,
          {
            key: child.key ?? `inline-child-${index}`,
          },
          sanitizeInlineChildren(child.props.children)
        )
      )
      return
    }

    nodes.push(cloneElement(child as React.ReactElement<any>, {
      key: child.key ?? `inline-child-${index}`,
    }))
  })

  return nodes
}

function sanitizeContainerChildren(children: ReactNode, textStyle: any = styles.paragraph): ReactNode[] {
  const nodes: ReactNode[] = []

  Children.forEach(children, (child, index) => {
    if (child == null || typeof child === 'boolean') {
      return
    }

    if (typeof child === 'string' || typeof child === 'number') {
      const value = String(child)

      if (!value.trim()) {
        return
      }

      nodes.push(
        <Text key={`container-text-${index}`} style={textStyle}>
          {value}
        </Text>
      )
      return
    }

    if (!isValidElement(child)) {
      return
    }

    if (typeof child.type === 'string') {
      const plainText = getPlainText(child.props?.children)
      if (!plainText) {
        return
      }
      nodes.push(
        <Text key={`container-dom-${index}`} style={textStyle}>
          {plainText}
        </Text>
      )
      return
    }

    const childKey = child.key ?? `container-child-${index}`
    const childChildren = child.props?.children
    const isTextLikeNode = child.type === Text || child.type === Link
    const isContainerNode = child.type === View || child.type === Page

    if (childChildren !== undefined) {
      if (isTextLikeNode) {
        nodes.push(
          cloneElement(
            child as React.ReactElement<any>,
            {
              key: childKey,
            },
            sanitizeInlineChildren(childChildren)
          )
        )
        return
      }

      if (isContainerNode) {
        nodes.push(
          cloneElement(
            child as React.ReactElement<any>,
            {
              key: childKey,
            },
            sanitizeContainerChildren(childChildren, textStyle)
          )
        )
        return
      }

      nodes.push(
        cloneElement(
          child as React.ReactElement<any>,
          {
            key: childKey,
          }
        )
      )
      return
    }

    nodes.push(
      cloneElement(
        child as React.ReactElement<any>,
        {
          key: childKey,
        }
      )
    )
  })

  return nodes
}

function SafePage({
  children,
  ...props
}: React.ComponentProps<typeof Page>) {
  return <Page {...props}>{sanitizeContainerChildren(children)}</Page>
}

function SafeView({
  children,
  textStyle = styles.paragraph,
  ...props
}: React.ComponentProps<typeof View> & { textStyle?: any }) {
  return <View {...props}>{sanitizeContainerChildren(children, textStyle)}</View>
}

function createHeadingIdResolver(entries: ReportPdfTocEntry[]) {
  const lookup = new Map<string, string>()
  const counts = new Map<string, number>()

  entries.forEach((entry) => {
    const normalizedTitle = entry.title.toLowerCase().trim()
    const count = counts.get(normalizedTitle) || 0
    const key = count === 0 ? normalizedTitle : `${normalizedTitle}__${count}`
    lookup.set(key, entry.id)
    counts.set(normalizedTitle, count + 1)
  })

  const seen = new Map<string, number>()

  return (title: string) => {
    const normalizedTitle = title.toLowerCase().trim()
    const count = seen.get(normalizedTitle) || 0
    const key = count === 0 ? normalizedTitle : `${normalizedTitle}__${count}`
    seen.set(normalizedTitle, count + 1)
    return lookup.get(key)
  }
}

function cloneListChildren(children: ReactNode, ordered: boolean) {
  return Children.map(children, (child, index) => {
    if (!isValidElement(child)) {
      return child
    }

    return cloneElement(child as React.ReactElement<any>, {
      ordered,
      itemIndex: index,
    })
  })
}

export function recordPageReference(pageReferences: Map<string, number>, id: string | undefined, pageNumber: number) {
  if (!id || !Number.isFinite(pageNumber)) {
    return
  }

  // react-pdf renders dynamic content twice; keep the latest observation
  // so second-pass pagination can replace first-pass placeholder values.
  pageReferences.set(id, pageNumber)
}

export function resolveTocPageNumber(pageReferences: Map<string, number>, id: string): string {
  const pageNumber = pageReferences.get(id)
  return pageNumber == null ? '' : String(pageNumber)
}

function PageReferenceProbe({
  id,
  pageReferences,
}: {
  id?: string
  pageReferences: Map<string, number>
}) {
  if (!id) {
    return null
  }

  return (
    <Text
      style={styles.pageReferenceProbe}
      render={({ pageNumber }) => {
        recordPageReference(pageReferences, id, pageNumber)
        return ''
      }}
    />
  )
}

function PdfMarkdown({
  markdown,
  tocEntries,
  pageReferences,
}: {
  markdown: string
  tocEntries: ReportPdfTocEntry[]
  pageReferences: Map<string, number>
}) {
  const resolveHeadingId = useMemo(() => createHeadingIdResolver(tocEntries), [tocEntries])
  const normalizedMarkdown = useMemo(() => normalizeMarkdownForPdf(markdown), [markdown])
  const firstTopLevelHeadingId = tocEntries.find((entry) => entry.level === 2)?.id

  const markdownComponents: any = {
    h1: ({ children }: any) => {
      const title = getPlainText(children)
      const id = resolveHeadingId(title)

      return (
        <View
          id={id}
          break={Boolean(id && firstTopLevelHeadingId && id !== firstTopLevelHeadingId)}
          style={styles.reportHeadingLevel1Container}
        >
          <PageReferenceProbe id={id} pageReferences={pageReferences} />
          <Text style={styles.reportHeadingLevel1}>{sanitizeInlineChildren(children)}</Text>
          <View style={styles.reportHeadingLevel1Rule} />
        </View>
      )
    },
    h2: ({ children }: any) => {
      const title = getPlainText(children)
      const id = resolveHeadingId(title)

      return (
        <SafeView id={id}>
          <PageReferenceProbe id={id} pageReferences={pageReferences} />
          <Text style={styles.reportHeadingLevel2}>{sanitizeInlineChildren(children)}</Text>
          <View style={styles.reportHeadingLevel2Rule} />
        </SafeView>
      )
    },
    h3: ({ children }: any) => {
      const title = getPlainText(children)
      const id = resolveHeadingId(title)

      return (
        <SafeView id={id}>
          <PageReferenceProbe id={id} pageReferences={pageReferences} />
          <Text style={styles.reportHeadingLevel3}>{sanitizeInlineChildren(children)}</Text>
        </SafeView>
      )
    },
    p: ({ children }: any) => (
      <Text style={styles.paragraph}>{sanitizeInlineChildren(children)}</Text>
    ),
    strong: ({ children }: any) => (
      <Text style={styles.strong}>{sanitizeInlineChildren(children)}</Text>
    ),
    em: ({ children }: any) => (
      <Text style={styles.em}>{sanitizeInlineChildren(children)}</Text>
    ),
    a: ({ href, children }: any) => (
      <Link src={href || ''} style={styles.link}>
        <Text>{getPlainText(children)}</Text>
      </Link>
    ),
    ul: ({ children }: any) => (
      <SafeView style={styles.list} textStyle={styles.listItemText}>
        {sanitizeContainerChildren(cloneListChildren(children, false), styles.listItemText)}
      </SafeView>
    ),
    ol: ({ children }: any) => (
      <SafeView style={styles.list} textStyle={styles.listItemText}>
        {sanitizeContainerChildren(cloneListChildren(children, true), styles.listItemText)}
      </SafeView>
    ),
    li: ({ children, ordered, itemIndex }: any) => (
      <SafeView style={styles.listItem} textStyle={styles.listItemText}>
        <Text style={styles.listBullet}>{ordered ? `${(itemIndex || 0) + 1}.` : '•'}</Text>
        <SafeView style={styles.listItemBody} textStyle={styles.listItemText}>
          {sanitizeContainerChildren(children, styles.listItemText)}
        </SafeView>
      </SafeView>
    ),
    blockquote: ({ children }: any) => (
      <SafeView style={styles.blockquote}>{sanitizeContainerChildren(children, styles.paragraph)}</SafeView>
    ),
    code: ({ inline, children }: any) => (
      <Text style={inline ? styles.inlineCode : styles.codeBlock}>{sanitizeInlineChildren(children)}</Text>
    ),
    pre: ({ children }: any) => (
      <SafeView style={styles.pre}>
        {getPlainText(children)
          .replace(/\r\n/g, '\n')
          .split('\n')
          .map((line, index) => (
            <Text key={`pre-line-${index}`} style={styles.codeBlock}>
              {line || ' '}
            </Text>
          ))}
      </SafeView>
    ),
    table: ({ children }: any) => (
      <SafeView style={styles.table}>{sanitizeContainerChildren(children)}</SafeView>
    ),
    thead: ({ children }: any) => (
      <SafeView style={styles.tableHead}>{sanitizeContainerChildren(children)}</SafeView>
    ),
    tbody: ({ children }: any) => (
      <SafeView style={styles.tableBody}>{sanitizeContainerChildren(children)}</SafeView>
    ),
    tr: ({ children }: any) => (
      <SafeView style={styles.tableRow}>{sanitizeContainerChildren(children)}</SafeView>
    ),
    th: ({ children }: any) => (
      <SafeView style={styles.tableCellHeader}>
        <Text style={styles.tableHeaderText}>{sanitizeInlineChildren(children)}</Text>
      </SafeView>
    ),
    td: ({ children }: any) => (
      <SafeView style={styles.tableCell}>
        <Text style={styles.tableCellText}>{sanitizeInlineChildren(children)}</Text>
      </SafeView>
    ),
    del: ({ children }: any) => <Text>{sanitizeInlineChildren(children)}</Text>,
    s: ({ children }: any) => <Text>{sanitizeInlineChildren(children)}</Text>,
    br: () => <Text>{'\n'}</Text>,
    input: ({ checked }: any) => <Text>{checked ? '[x] ' : '[ ] '}</Text>,
    hr: () => <SafeView style={{ borderBottomWidth: 1, borderBottomColor: '#e2e8f0', marginBottom: 12 }} />,
  }

  return (
    <ReactMarkdown
      remarkPlugins={PDF_REMARK_PLUGINS}
      rehypePlugins={PDF_REHYPE_PLUGINS}
      components={markdownComponents}
      allowedElements={[...SAFE_MARKDOWN_ELEMENTS]}
      unwrapDisallowed
      skipHtml
    >
      {normalizedMarkdown}
    </ReactMarkdown>
  )
}

interface NumberedTocEntry {
  id: string
  title: string
  level: 2 | 3
  label: string
}

function buildNumberedTocEntries(data: ReportPdfModel): NumberedTocEntry[] {
  const raw: Array<{ id: string; title: string; level: 2 | 3 }> = []

  if (data.summary) {
    raw.push({ id: 'executive-summary', title: 'Executive Summary', level: 2 })
  }
  if (data.keyFindings.length > 0) {
    raw.push({ id: 'key-findings', title: 'Key Findings', level: 2 })
  }
  if (data.reportMarkdown) {
    raw.push({ id: 'full-report', title: 'Full Report', level: 2 })
    raw.push(...data.tocEntries)
  }
  if (data.references.length > 0) {
    raw.push({ id: 'references', title: 'References', level: 2 })
  }

  let majorIndex = 0
  let minorIndex = 0

  return raw.map((entry) => {
    if (entry.level === 2) {
      majorIndex += 1
      minorIndex = 0
      return { ...entry, label: `${majorIndex}` }
    }
    minorIndex += 1
    return { ...entry, label: `${majorIndex}.${minorIndex}` }
  })
}

function softWrapUrl(url: string): string {
  try {
    const parsed = new URL(url)
    const display = `${parsed.hostname}${parsed.pathname}`.replace(/\/$/, '') || parsed.hostname
    return display.length > 96 ? `${display.slice(0, 93)}...` : display
  } catch {
    return url.length > 96 ? `${url.slice(0, 93)}...` : url
  }
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result)
        return
      }

      reject(new Error('Failed to read image blob as data URL'))
    }

    reader.onerror = () => {
      reject(reader.error || new Error('Failed to read image blob'))
    }

    reader.readAsDataURL(blob)
  })
}

function resolvePdfUrl(raw: string): URL | null {
  try {
    const base = typeof window !== 'undefined' ? window.location.origin : API_BASE_URL
    return new URL(raw, base)
  } catch {
    return null
  }
}

function shouldAttachPdfAuthHeaders(fetchSrc: string): boolean {
  const targetUrl = resolvePdfUrl(fetchSrc)
  const apiUrl = resolvePdfUrl(API_BASE_URL)

  return Boolean(targetUrl && apiUrl && targetUrl.origin === apiUrl.origin)
}

function isPdfImageMetadataEndpoint(src: string): boolean {
  const resolvedUrl = resolvePdfUrl(src)
  const pathname = resolvedUrl?.pathname || src
  return /^\/research\/\d+\/image\/?$/.test(pathname)
}

function isLikelyDirectImageUrl(src: string): boolean {
  const resolvedUrl = resolvePdfUrl(src)
  const pathname = resolvedUrl?.pathname || src

  return (
    /\.(png|jpe?g|gif|webp|svg)$/i.test(pathname) ||
    /^\/api\/intelligence\/report\/[^/]+\/image\/?$/.test(pathname)
  )
}

function buildPdfImageRequestInit(fetchSrc: string, init?: RequestInit): RequestInit {
  const headers = new Headers(init?.headers || undefined)

  if (shouldAttachPdfAuthHeaders(fetchSrc)) {
    Object.entries(getAuthHeader()).forEach(([key, value]) => {
      headers.set(key, value)
    })
  }

  if (!headers.has('Accept')) {
    headers.set('Accept', 'image/*,application/json;q=0.9,*/*;q=0.8')
  }

  return {
    ...init,
    headers,
  }
}

class PdfImageFetchError extends Error {
  reason: 'network' | 'non-image'

  constructor(message: string, reason: 'network' | 'non-image') {
    super(message)
    this.name = 'PdfImageFetchError'
    this.reason = reason
  }
}

async function resolvePdfImageUrlInternal(
  src: string | undefined,
  visited: Set<string>,
): Promise<string | undefined> {
  if (!src) {
    return undefined
  }

  if (src.startsWith('data:') || src.startsWith('blob:')) {
    return src
  }

  const normalizedSrc = resolveAssetUrl(src, API_BASE_URL) || src
  if (visited.has(normalizedSrc)) {
    throw new PdfImageFetchError(`Detected recursive image source for ${normalizedSrc}`, 'non-image')
  }

  visited.add(normalizedSrc)

  async function tryResolve(fetchSrc: string, init?: RequestInit): Promise<string> {
    let response: Response

    try {
      response = init
        ? await fetch(fetchSrc, buildPdfImageRequestInit(fetchSrc, init))
        : await fetch(fetchSrc, buildPdfImageRequestInit(fetchSrc))
    } catch (error: any) {
      throw new PdfImageFetchError(error?.message || 'Failed to fetch image', 'network')
    }

    if (!response.ok) {
      throw new PdfImageFetchError(`Failed to load image (${response.status})`, 'network')
    }

    const contentType = response.headers.get('content-type') || ''
    if (contentType.includes('application/json')) {
      let payload: any = null

      try {
        payload = typeof response.json === 'function' ? await response.json() : null
      } catch {
        payload = null
      }

      const nestedSrc =
        typeof payload?.image_url === 'string'
          ? resolveAssetUrl(payload.image_url, API_BASE_URL) || payload.image_url
          : undefined

      if (nestedSrc) {
        if (isLikelyDirectImageUrl(nestedSrc)) {
          return nestedSrc
        }
        return (await resolvePdfImageUrlInternal(nestedSrc, visited)) || nestedSrc
      }

      throw new PdfImageFetchError(`Failed to load image (received ${contentType})`, 'non-image')
    }

    if (contentType && !contentType.startsWith('image/')) {
      throw new PdfImageFetchError(`Failed to load image (received ${contentType})`, 'non-image')
    }

    return fetchSrc
  }

  const errors: Error[] = []

  try {
    return await tryResolve(normalizedSrc)
  } catch (error) {
    errors.push(error as Error)
  }

  try {
    return await tryResolve(normalizedSrc, { credentials: 'omit' })
  } catch (error) {
    errors.push(error as Error)
  }

  if (errors.some((error) => error instanceof PdfImageFetchError && error.reason === 'non-image')) {
    console.warn('Failed to prepare PDF cover image:', errors)
    return undefined
  }

  console.warn('Failed to resolve PDF cover image URL; falling back to direct URL:', normalizedSrc, errors)
  return normalizedSrc
}

export async function resolvePdfImageUrl(src?: string): Promise<string | undefined> {
  return resolvePdfImageUrlInternal(src, new Set<string>())
}

export async function resolvePdfImageSource(src?: string): Promise<string | undefined> {
  if (!src) {
    return undefined
  }

  const resolvedSrc = isPdfImageMetadataEndpoint(src)
    ? await resolvePdfImageUrl(src)
    : resolveAssetUrl(src, API_BASE_URL) || src

  if (!resolvedSrc) {
    return undefined
  }

  if (resolvedSrc.startsWith('data:') || resolvedSrc.startsWith('blob:')) {
    return resolvedSrc
  }

  async function tryInline(fetchSrc: string, init?: RequestInit): Promise<string> {
    let response: Response

    try {
      response = init
        ? await fetch(fetchSrc, buildPdfImageRequestInit(fetchSrc, init))
        : await fetch(fetchSrc, buildPdfImageRequestInit(fetchSrc))
    } catch (error: any) {
      throw new PdfImageFetchError(error?.message || 'Failed to fetch image', 'network')
    }

    if (!response.ok) {
      throw new PdfImageFetchError(`Failed to load image (${response.status})`, 'network')
    }

    const contentType = response.headers.get('content-type') || ''
    if (contentType && !contentType.startsWith('image/')) {
      throw new PdfImageFetchError(`Failed to load image (received ${contentType})`, 'non-image')
    }

    const blob = await response.blob()
    if (blob.type && !blob.type.startsWith('image/')) {
      throw new PdfImageFetchError(`Failed to load image (received ${blob.type})`, 'non-image')
    }

    return blobToDataUrl(blob)
  }

  const errors: Error[] = []

  try {
    return await tryInline(resolvedSrc)
  } catch (error) {
    errors.push(error as Error)
  }

  try {
    return await tryInline(resolvedSrc, { credentials: 'omit' })
  } catch (error) {
    errors.push(error as Error)
  }

  if (errors.some((error) => error instanceof PdfImageFetchError && error.reason === 'non-image')) {
    console.warn('Failed to prepare PDF cover image:', errors)
    return undefined
  }

  console.warn('Failed to inline PDF cover image; falling back to direct URL:', resolvedSrc, errors)
  return resolvedSrc
}

export function ReportPdfDocument({ data }: { data: ReportPdfModel }) {
  const tocEntries = buildNumberedTocEntries(data)
  const pageReferences = useMemo(() => new Map<string, number>(), [])

  return (
    <Document title={data.title} author="Insight Atlas" subject={data.title}>
      {[
        <Page key="cover" size="A4" style={styles.coverPage} wrap={false}>
          <View style={styles.coverCanvas}>
            <View style={styles.coverImageFrame}>
              <View style={data.coverImageUrl ? styles.coverBg : styles.coverBgNoImage} />
              {data.coverImageUrl ? <Image style={styles.coverImage} src={data.coverImageUrl} /> : null}
              {data.coverImageUrl ? <View style={styles.coverOverlayLight} /> : null}
              {data.coverImageUrl ? <View style={styles.coverOverlayHeavy} /> : null}
            </View>
            <View style={styles.coverContent}>
              <Text style={styles.coverKicker}>INSIGHT ATLAS AI</Text>
              {data.category ? (
                <View style={styles.coverCategory}>
                  <Text style={styles.coverCategoryText}>{data.category}</Text>
                </View>
              ) : null}
              <Text style={styles.coverTitle}>{data.title}</Text>
              <View style={styles.coverRule} />
              {data.generatedLabel ? <Text style={styles.coverMeta}>{data.generatedLabel}</Text> : null}
              {data.preparedBy ? <Text style={styles.coverMeta}>{data.preparedBy}</Text> : null}
            </View>
          </View>
        </Page>,

        <Page key="toc" size="A4" style={styles.tocPage} wrap={false}>
          {[
            <View key="toc-header" style={styles.tocHeader}>
              <Text style={styles.tocTitle}>Table of Contents</Text>
            </View>,
            <View key="toc-list" style={styles.tocList}>
              {tocEntries.map((entry) => (
                <View
                  key={`${entry.id}-${entry.title}`}
                  style={entry.level === 3 ? styles.tocItemLevel3 : styles.tocItemLevel2}
                >
                  <View style={styles.tocNumberBox}>
                    <Text style={styles.tocNumberText}>{`${entry.label}.`}</Text>
                  </View>
                  <View style={styles.tocContent}>
                    <Link
                      src={`#${entry.id}`}
                      style={entry.level === 3 ? styles.tocLinkLevel3 : styles.tocLinkLevel2}
                    >
                      <Text>{entry.title}</Text>
                    </Link>
                    <Text
                      style={styles.tocPageNumber}
                      render={() => resolveTocPageNumber(pageReferences, entry.id)}
                    />
                  </View>
                </View>
              ))}
            </View>,
          ]}
        </Page>,

        <SafePage key="content" size="A4" style={styles.contentPage} wrap>
          <Text style={styles.headerTitle}>{data.title}</Text>
          {data.generatedLabel || data.preparedBy ? (
            <Text style={styles.headerMeta}>
              {[data.generatedLabel, data.preparedBy].filter(Boolean).join('  |  ')}
            </Text>
          ) : null}

          {data.summary ? (
            <SafeView id="executive-summary" style={styles.section}>
              <PageReferenceProbe id="executive-summary" pageReferences={pageReferences} />
              <Text style={styles.sectionHeading}>Executive Summary</Text>
              <View style={styles.sectionRule} />
              <PdfMarkdown markdown={data.summary} tocEntries={[]} pageReferences={pageReferences} />
            </SafeView>
          ) : null}

          {data.keyFindings.length > 0 ? (
            <SafeView id="key-findings" style={styles.section}>
              <PageReferenceProbe id="key-findings" pageReferences={pageReferences} />
              <Text style={styles.sectionHeading}>Key Findings</Text>
              <View style={styles.sectionRule} />
              <SafeView style={styles.findingsList}>
                {data.keyFindings.map((finding, index) => (
                  <SafeView key={`${index}-${finding.slice(0, 24)}`} style={styles.listItem}>
                    <Text style={styles.listBullet}>{`${index + 1}.`}</Text>
                    <SafeView style={styles.listItemBody}>
                      <PdfMarkdown markdown={finding} tocEntries={[]} pageReferences={pageReferences} />
                    </SafeView>
                  </SafeView>
                ))}
              </SafeView>
            </SafeView>
          ) : null}

          {data.reportMarkdown ? (
            <>
              <View id="full-report" style={styles.reportSectionAnchor}>
                <PageReferenceProbe id="full-report" pageReferences={pageReferences} />
              </View>
              <PdfMarkdown
                markdown={data.reportMarkdown}
                tocEntries={data.tocEntries}
                pageReferences={pageReferences}
              />
            </>
          ) : null}

          {data.references.length > 0 ? (
            <SafeView id="references" style={styles.section}>
              <PageReferenceProbe id="references" pageReferences={pageReferences} />
              <Text style={styles.sectionHeading}>References</Text>
              <View style={styles.sectionRule} />
              <SafeView style={styles.referencesList}>
                {data.references.map((reference) => (
                  <SafeView key={reference.number} style={styles.referenceItem}>
                    <Text style={styles.referenceNumber}>[{String(reference.number)}]</Text>
                    <Link src={reference.url} style={styles.referenceLink}>
                      <Text>{softWrapUrl(reference.url)}</Text>
                    </Link>
                  </SafeView>
                ))}
              </SafeView>
            </SafeView>
          ) : null}

          <Text
            fixed
            style={styles.pageNumber}
            render={({ pageNumber, totalPages }) =>
              pageNumber > 1 ? `Page ${pageNumber} of ${totalPages}` : ''
            }
          />
        </SafePage>,
      ]}
    </Document>
  )
}

function createPdfFilename(title: string): string {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

  return `${slug || 'research-report'}.pdf`
}

export async function downloadReportPdf(data: ReportPdfModel): Promise<void> {
  const resolvedImage = await resolvePdfImageSource(data.coverImageUrl ?? undefined)
  const pdfData = {
    ...data,
    coverImageUrl: resolvedImage,
  }
  const blob = await pdf(<ReportPdfDocument data={pdfData} />).toBlob()
  const blobUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')

  link.href = blobUrl
  link.download = createPdfFilename(data.title)
  link.rel = 'noopener'
  link.style.display = 'none'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  // Let the browser start consuming the blob before cleanup.
  window.setTimeout(() => {
    URL.revokeObjectURL(blobUrl)
  }, 1000)
}
